import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Eula, ForgotPassword, User, UserAction } from '@core/models';
import { AppState } from '@core/store/reducers';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { firstValueFrom, Observable, map } from 'rxjs';
import { IUser } from '../models/user/i-user';
import { acceptEula, loadUserSuccess, setConnection } from '../store/actions/user.actions';
import { UsersState } from '../store/reducers/users.reducer';
import { ConfigService } from './config.service';
import { ConnectionResponse } from '../models/user/connection-response';

@Injectable()
export class UsersService {
  publicPath: string;
  uoPath: string;
  baseUserPath: string;
  state: Observable<UsersState>;

  user: User;
  publicPaths: string[];
  userPromise: Promise<User>;
  firstTime = true;


  constructor(private readonly configService: ConfigService, private readonly httpClient: HttpClient, private readonly store: Store<AppState>) {
    this.publicPath = this.configService.appConfig.api.publicPath;
    this.uoPath = this.configService.appConfig.api.uoUrl;
    this.baseUserPath = this.configService.appConfig.api.uoUrl + '/user';
  }

  async pubtk(): Promise<void | ConnectionResponse> {
    return await firstValueFrom(this.httpClient
      .post<ConnectionResponse>(`${this.publicPath}/uac/pubtk`, '', {
        observe: 'response'
      }))
      .then((response) => {
        const tkn = response.headers.get('Set-Tkn');
        if (tkn) {
          const publicPaths = response.body.publicPaths;
          sessionStorage.setItem('pubtk', tkn);
          this.store.dispatch(setConnection({ usersState: { tkn: tkn, publicPaths: publicPaths } }));
        }
        return response.body;
      })
      .catch((err: HttpErrorResponse) => {
        console.error('An error occurred:', err.message);
      });
  }

  authorizeUser(): Promise<IUser> {
    return firstValueFrom(this.httpClient
      .get<IUser>(this.configService.appConfig.api.uacUrl + "/authorize"))
  }

  loadUser(): Promise<User> {
    return firstValueFrom(this.httpClient
      .post<IUser>(this.baseUserPath + "/getUser", null))
      .then((u) => {
        const user = User.userFactory(u);
        this.store.dispatch(loadUserSuccess({ user: user }));
        return user;
      });
  }

  getUsers(): Observable<User[]> {
    return this.httpClient.get<User[]>(`${this.baseUserPath}/list`);
  }

  getOrgUsers(orgId: number): Observable<User[]> {
    return this.httpClient.get<User[]>(`${this.baseUserPath}/getOrgUsers/${orgId}`).pipe(
      map((response) => {
        const users: User[] = [];

        response.forEach((userData) => {
          users.push(User.userFactory(userData));
        });
        return users;
      })
    );
  }

  createOrUpdate(userAction: UserAction): Observable<UserAction> {
    if (userAction.user.userId) {
      return this.httpClient.put<User>(`${this.baseUserPath}/edit`, userAction.user).pipe(map(response => {
        if (response) {
          return userAction;
        }
      }));
    } else {
      return this.httpClient.post<User>(`${this.baseUserPath}/create`, userAction.user).pipe(map(response => {
        if (response) {
          return userAction
        }
      }));
    }
  }

  delete(userId: number): Observable<any> {
    return this.httpClient.delete(`${this.baseUserPath}/delete/${userId}`);
  }

  resendActivation(forgotPasswordData: ForgotPassword): Promise<any> {
    return firstValueFrom(this.httpClient
      .post(`${this.baseUserPath}/resendActivation`, forgotPasswordData, { observe: 'response' }));
  }

  getEula(): Promise<string> {
    return firstValueFrom(this.httpClient
      .get<Eula>(`${this.uoPath}/eula/text`))
      .then((response) => response.eulaText);
  }

  acceptEula(userData: User): Promise<User> {
    const now = moment().format('YYYY-MM-dd');
    return firstValueFrom(this.httpClient
      .put(`${this.uoPath}/user/eula`, ''))
      .then(() => {
        const userInfo = User.userFactory(userData);
        userInfo.eulaAcceptedTime = now;
        const userAction = {
          user: userInfo
        } as UserAction;

        this.store.dispatch(acceptEula({ userAction: userAction }));
        return userInfo;
      });
  }

  updatePreference(user: User): Promise<User> {
    return firstValueFrom(this.httpClient.put<User>(`${this.baseUserPath}/edit`, user));
  }

  checkUser(userLoginName: string): Promise<any> {
    return firstValueFrom(this.httpClient.post<User>(`${this.uoPath}/checkUser`, { "userLoginName": userLoginName }));
  }

}